<template>
    <div class="apt-page">
        <BackendHeader />
        <div class="apt-container">
            <div class="apt-header">
                <h2>Audience Pair Tuner — MommyDeals</h2>
                <div class="apt-stats" v-if="stats">
                    <span class="apt-stat"><strong>{{ stats.pairs }}</strong> pairs</span>
                    <span class="apt-stat" v-if="stats.decided > 0"><strong>{{ stats.decided }}</strong> decided</span>
                    <span class="apt-stat" v-if="stats.ties != null"><strong>{{ stats.ties }}</strong> ties</span>
                    <span class="apt-stat" v-if="stats.skips != null"><strong>{{ stats.skips }}</strong> skips</span>
                    <span class="apt-stat" v-if="stats.modelAgreementPct != null">model agrees <strong>{{ stats.modelAgreementPct }}%</strong></span>
                    <button @click="loadBatch" class="apt-btn apt-btn-sm">Refresh</button>
                </div>
            </div>

            <p class="apt-instructions">
                <strong>Which deal is better for a household-running mom?</strong>
                <kbd>←</kbd> A wins · <kbd>→</kbd> B wins · <kbd>=</kbd> both good · <kbd>N</kbd> skip / can't tell
            </p>

            <!-- Pair model train banner (independent of label/clicky bars). -->
            <TrainNowBar
                kind="pair"
                title="Pair model (LambdaRank on your A/B picks)"
                :status="trainingStatus?.pairModel"
                :loading="trainingStatusLoading"
                :triggering="trainingTriggering"
                @train="onTrainClick"
                @refresh="loadTrainingStatus"
            />

            <div v-if="loading" class="apt-loading">Loading pairs…</div>
            <div v-else-if="done" class="apt-done">No more pairs. Come back later or refresh.</div>

            <template v-else>
                <div class="apt-progress">{{ judgedCount }} / {{ pairs.length }} judged in this batch</div>

                <!-- Active pair (centered, big). All other pairs collapse below
                     once judged so the operator's eye stays on the current one. -->
                <div v-for="(pair, idx) in pairs" :key="pairKey(pair, idx)"
                     v-show="idx === activeIdx || !judged.has(pairKey(pair, idx))"
                     :class="['apt-pair', { 'apt-pair-active': idx === activeIdx, 'apt-pair-done': judged.has(pairKey(pair, idx)) }]">

                    <div class="apt-pair-deal apt-side-a" @click="submit(idx, 'A')">
                        <div class="apt-side-label">A · ←</div>
                        <div class="apt-deal-img">
                            <ImagePreview v-if="firstImage(pair.dealA)" :imageUrl="firstImage(pair.dealA)!" :maxWidth="220" :maxHeight="220" :finalWidth="600" :finalHeight="600" />
                            <div v-else class="apt-no-img">no img</div>
                        </div>
                        <div class="apt-deal-title">{{ truncate(pair.dealA.title, 100) }}</div>
                        <div class="apt-deal-meta">
                            <span v-if="pair.dealA.brand" class="apt-meta-item">{{ pair.dealA.brand }}</span>
                            <span v-if="pair.dealA.finalPrice" class="apt-meta-item apt-meta-price">${{ Number(pair.dealA.finalPrice).toFixed(2) }}</span>
                            <span v-if="pair.dealA.retailPrice && pair.dealA.retailPrice > pair.dealA.finalPrice" class="apt-meta-item apt-meta-retail">
                                <s>${{ Number(pair.dealA.retailPrice).toFixed(2) }}</s>
                            </span>
                            <span v-if="pair.dealA.category" class="apt-meta-item apt-meta-cat">{{ pair.dealA.category }}</span>
                            <span v-if="pair.dealA.effectiveGrade" :class="['apt-meta-item', `grade-${(pair.dealA.effectiveGrade||'').toLowerCase()}`]">{{ pair.dealA.effectiveGrade }}</span>
                            <span class="apt-meta-item apt-meta-model">model: {{ pair.dealA.modelScore }}</span>
                            <a v-if="pair.dealA.productLink" :href="pair.dealA.productLink" target="_blank" class="apt-link" @click.stop>↗</a>
                        </div>
                    </div>

                    <div class="apt-pair-vs">
                        <div class="apt-vs-text">vs</div>
                        <div class="apt-pair-actions">
                            <button class="apt-act apt-act-tie" @click="submit(idx, 'T')" title="Both good (=)"><span>=</span> tie</button>
                            <button class="apt-act apt-act-skip" @click="submit(idx, 'N')" title="Skip / can't tell (N)"><span>N</span> skip</button>
                        </div>
                    </div>

                    <div class="apt-pair-deal apt-side-b" @click="submit(idx, 'B')">
                        <div class="apt-side-label">B · →</div>
                        <div class="apt-deal-img">
                            <ImagePreview v-if="firstImage(pair.dealB)" :imageUrl="firstImage(pair.dealB)!" :maxWidth="220" :maxHeight="220" :finalWidth="600" :finalHeight="600" />
                            <div v-else class="apt-no-img">no img</div>
                        </div>
                        <div class="apt-deal-title">{{ truncate(pair.dealB.title, 100) }}</div>
                        <div class="apt-deal-meta">
                            <span v-if="pair.dealB.brand" class="apt-meta-item">{{ pair.dealB.brand }}</span>
                            <span v-if="pair.dealB.finalPrice" class="apt-meta-item apt-meta-price">${{ Number(pair.dealB.finalPrice).toFixed(2) }}</span>
                            <span v-if="pair.dealB.retailPrice && pair.dealB.retailPrice > pair.dealB.finalPrice" class="apt-meta-item apt-meta-retail">
                                <s>${{ Number(pair.dealB.retailPrice).toFixed(2) }}</s>
                            </span>
                            <span v-if="pair.dealB.category" class="apt-meta-item apt-meta-cat">{{ pair.dealB.category }}</span>
                            <span v-if="pair.dealB.effectiveGrade" :class="['apt-meta-item', `grade-${(pair.dealB.effectiveGrade||'').toLowerCase()}`]">{{ pair.dealB.effectiveGrade }}</span>
                            <span class="apt-meta-item apt-meta-model">model: {{ pair.dealB.modelScore }}</span>
                            <a v-if="pair.dealB.productLink" :href="pair.dealB.productLink" target="_blank" class="apt-link" @click.stop>↗</a>
                        </div>
                    </div>
                </div>

                <div class="apt-footer">
                    <span class="apt-progress">{{ judgedCount }} / {{ pairs.length }} judged</span>
                    <button @click="loadBatch" class="apt-btn">Load Next Batch</button>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import BackendHeader from '@/components/BackendHeader.vue'
import ImagePreview from '@/components/Backend/ImagePreview.vue'
import TrainNowBar from '@/components/TrainNowBar.vue'
import api from '@/api'

interface PairDeal {
    dealId: number
    title: string | null
    brand: string | null
    finalPrice: number | null
    retailPrice: number | null
    imagesJson: string | null
    promoCode: string | null
    productLink: string | null
    source: string | null
    asin: string | null
    category: string | null
    modelScore: number
    modelConfidence: number
    modelVersion: string | null
    pairModelScore: number | null
    effectiveGrade: string | null
}
interface Pair { dealA: PairDeal; dealB: PairDeal }

const pairs = ref<Pair[]>([])
const loading = ref(false)
const done = ref(false)
const activeIdx = ref(0)
const judged = ref(new Set<string>())
const stats = ref<any>(null)

// Pair-model train banner state. Same pattern as AudienceTuner.vue:
// poll while training, refresh on completion.
const trainingStatus = ref<any>(null)
const trainingStatusLoading = ref(false)
const trainingTriggering = ref(false)
let trainingPollTimer: ReturnType<typeof setInterval> | null = null

const pairKey = (p: Pair, idx: number) => `${idx}-${p.dealA.dealId}-${p.dealB.dealId}`
const judgedCount = computed(() => judged.value.size)

const loadStats = async () => {
    try {
        const resp = await api.getAudiencePairTunerStats()
        stats.value = resp.data
    } catch { /* non-fatal */ }
}

const loadTrainingStatus = async () => {
    trainingStatusLoading.value = true
    try {
        const resp = await api.getTrainingStatus()
        trainingStatus.value = resp.data
    } catch { /* keep last */ }
    finally { trainingStatusLoading.value = false }
}

const onTrainClick = async () => {
    if (trainingTriggering.value) return
    trainingTriggering.value = true
    try {
        await api.trainPairModel()
        if (trainingPollTimer) clearInterval(trainingPollTimer)
        trainingPollTimer = setInterval(async () => {
            await loadTrainingStatus()
            if (trainingStatus.value?.pairModel?.status !== 'training') {
                if (trainingPollTimer) { clearInterval(trainingPollTimer); trainingPollTimer = null }
            }
        }, 5000)
        await loadTrainingStatus()
    } catch (e) {
        console.error('Trigger pair train failed', e)
    } finally {
        trainingTriggering.value = false
    }
}

const loadBatch = async () => {
    loading.value = true
    try {
        const resp = await api.getAudiencePairBatch('mommydeals', 10)
        if (resp.data.done) {
            done.value = true
            pairs.value = []
        } else {
            pairs.value = resp.data.pairs
            judged.value = new Set()
            activeIdx.value = 0
            done.value = false
        }
    } catch {
        done.value = true
    } finally {
        loading.value = false
    }
}

const submit = async (idx: number, winner: 'A' | 'B' | 'T' | 'N') => {
    const pair = pairs.value[idx]
    if (!pair) return
    const key = pairKey(pair, idx)
    if (judged.value.has(key)) return

    try {
        await api.saveAudiencePairJudgment({
            dealIdA: pair.dealA.dealId,
            dealIdB: pair.dealB.dealId,
            winner,
            modelScoreA: pair.dealA.modelScore,
            modelScoreB: pair.dealB.modelScore,
            modelVersionAtPair: pair.dealA.modelVersion ?? pair.dealB.modelVersion ?? null,
        })
        judged.value = new Set([...judged.value, key])
        await loadStats()

        // Advance to next un-judged pair, else load next batch.
        const next = pairs.value.findIndex((p, i) => i > idx && !judged.value.has(pairKey(p, i)))
        if (next >= 0) activeIdx.value = next
        else if (judged.value.size >= pairs.value.length) await loadBatch()
    } catch (e) {
        console.error('Save pair judgment failed', e)
    }
}

const firstImage = (deal: PairDeal): string | null => {
    if (!deal.imagesJson) return null
    try {
        const arr = JSON.parse(deal.imagesJson)
        return Array.isArray(arr) && arr.length > 0 ? arr[0] : typeof arr === 'string' ? arr : null
    } catch { return null }
}
const truncate = (s: string | null, n: number) => s && s.length > n ? s.slice(0, n) + '…' : s || ''

const onKeyDown = (e: KeyboardEvent) => {
    if (loading.value || done.value || pairs.value.length === 0) return
    const target = e.target as HTMLElement | null
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return
    const idx = activeIdx.value
    if (e.key === 'ArrowLeft')      submit(idx, 'A')
    else if (e.key === 'ArrowRight') submit(idx, 'B')
    else if (e.key === '=' || e.key === '+') submit(idx, 'T')
    else if (e.key === 'n' || e.key === 'N') submit(idx, 'N')
}

onMounted(() => {
    loadStats()
    loadBatch()
    loadTrainingStatus()
    window.addEventListener('keydown', onKeyDown)
})
onBeforeUnmount(() => {
    window.removeEventListener('keydown', onKeyDown)
    if (trainingPollTimer) clearInterval(trainingPollTimer)
})
</script>

<style scoped>
.apt-page { min-height: 100vh; background: #f5f5f5; padding: 20px; text-align: left; }
.apt-container { max-width: 1400px; margin: 0 auto; background: #fff; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); padding: 20px; }
.apt-header { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 12px; padding-bottom: 12px; border-bottom: 2px solid #e0e0e0; flex-wrap: wrap; }
.apt-header h2 { margin: 0; color: #333; font-size: 18px; }
.apt-stats { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; font-size: 12px; color: #555; }
.apt-stat { padding: 3px 10px; background: #eceff1; border-radius: 12px; }
.apt-instructions { font-size: 13px; color: #666; margin: 0 0 16px 0; }
.apt-instructions kbd { background: #eceff1; padding: 1px 5px; border-radius: 3px; font-size: 11px; margin: 0 2px; }
.apt-loading, .apt-done { text-align: center; padding: 40px; color: #999; font-size: 14px; }
.apt-progress { font-size: 13px; color: #666; margin-bottom: 12px; }

.apt-pair {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    gap: 14px;
    padding: 14px;
    border: 2px solid #e0e0e0;
    border-radius: 10px;
    margin-bottom: 14px;
    align-items: stretch;
    background: #fff;
}
.apt-pair-active { border-color: #1565c0; background: #f8f9ff; padding: 24px; }
.apt-pair-done { display: none; }

.apt-pair-deal {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 12px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.15s;
    background: #fafafa;
}
.apt-pair-deal:hover { border-color: #1565c0; background: #f5f9ff; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(21,101,192,0.15); }
.apt-pair-active .apt-pair-deal { padding: 16px; }

.apt-side-label { font-size: 14px; font-weight: 700; color: #1565c0; margin-bottom: 8px; letter-spacing: 1px; }
.apt-deal-img { width: 220px; height: 220px; flex-shrink: 0; }
.apt-pair-active .apt-deal-img { width: 360px; height: 360px; }
.apt-pair-active :deep(.imgPreview img) { max-width: 360px !important; max-height: 360px !important; }
.apt-no-img { width: 100%; height: 100%; background: #f5f5f5; border-radius: 4px; display: flex; align-items: center; justify-content: center; font-size: 11px; color: #bbb; }

.apt-deal-title { font-size: 13px; font-weight: 600; color: #333; margin-top: 10px; line-height: 1.3; }
.apt-deal-meta { display: flex; gap: 6px; flex-wrap: wrap; justify-content: center; margin-top: 8px; font-size: 11px; color: #666; }
.apt-meta-item { padding: 1px 6px; background: #f5f5f5; border-radius: 4px; }
.apt-meta-cat { background: #e3f2fd; color: #1565c0; }
.apt-meta-price { background: #e8f5e9; color: #2e7d32; font-weight: 700; }
.apt-meta-retail { color: #999; }
.apt-meta-model { background: #fff3e0; color: #e65100; font-family: monospace; }
.apt-link { color: #1565c0; text-decoration: none; font-size: 14px; }

.apt-pair-vs {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
    min-width: 90px;
}
.apt-vs-text { font-size: 22px; color: #aaa; font-weight: 700; }
.apt-pair-actions { display: flex; flex-direction: column; gap: 8px; }
.apt-act {
    padding: 8px 14px;
    border: 1px solid #cfd8dc;
    border-radius: 6px;
    background: #fff;
    cursor: pointer;
    font-size: 12px;
    color: #555;
    transition: all 0.1s;
}
.apt-act:hover { transform: scale(1.05); }
.apt-act-tie { border-color: #2e7d32; color: #2e7d32; }
.apt-act-tie:hover { background: #2e7d32; color: #fff; }
.apt-act-skip { border-color: #9e9e9e; color: #757575; }
.apt-act-skip:hover { background: #757575; color: #fff; }
.apt-act span { display: inline-block; min-width: 14px; font-weight: 700; margin-right: 4px; }

.apt-footer { display: flex; justify-content: space-between; align-items: center; margin-top: 16px; padding-top: 12px; border-top: 1px solid #eee; }
.apt-btn { padding: 8px 16px; background: #1565c0; color: #fff; border: none; border-radius: 4px; cursor: pointer; font-size: 13px; }
.apt-btn:hover { background: #0d47a1; }
.apt-btn-sm { padding: 4px 10px; font-size: 11px; }

.grade-great { background: #2e7d32; color: #fff; border-radius: 10px; padding: 1px 6px; font-size: 10px; font-weight: 700; }
.grade-good { background: #66bb6a; color: #fff; border-radius: 10px; padding: 1px 6px; font-size: 10px; font-weight: 700; }
.grade-sale { background: #fff3c4; color: #8a6d00; border-radius: 10px; padding: 1px 6px; font-size: 10px; font-weight: 700; }
.grade-regular { background: #eceff1; color: #555; border-radius: 10px; padding: 1px 6px; font-size: 10px; font-weight: 700; }
.grade-notadeal { background: #d32f2f; color: #fff; border-radius: 10px; padding: 1px 6px; font-size: 10px; font-weight: 700; }
</style>
